import React from 'react';
import { useSelect } from '@refinedev/antd';
import { Form, Input, InputNumber, Select, Switch, Alert } from 'antd';
import { LocalizedContentTabs, type LocalizedFieldConfig } from '../../components/LocalizedContentTabs';
import { ArtworkInput, ArtworkListInput } from './artworkUpload';

/** `name` is localised; everything else on a skin is language-neutral. */
export const PET_SKIN_LANG_FIELDS: LocalizedFieldConfig[] = [
  { key: 'name', label: 'Name', required: true },
];

/**
 * Artwork paths are relative to the media root — committed files live in
 * `packages/api/prisma/pet-skin-images/` and are served from `pet-skins/`;
 * Upload stores a file unchanged and fills its path in. An absolute http(s)
 * URL is passed through untouched.
 */
const PATH_HINT =
  'Upload a transparent PNG or WebP, or type a path such as pet-skins/egg_waffle_card.png';

const MOOD_ORDER = 'drained, low, chill, good, thriving';

/** Home needs one per mood: all five or none (the API rejects anything else). */
const MOOD_SET_RULE = {
  validator: (_: unknown, value?: string[]) =>
    !value?.length || value.length === 5
      ? Promise.resolve()
      : Promise.reject(new Error(`Add all five (${MOOD_ORDER}) or leave it empty`)),
};

export const PetSkinFormFields: React.FC<{ slugEditable: boolean }> = ({ slugEditable }) => {
  const { selectProps: categorySelectProps } = useSelect({
    resource: 'pet-skin-categories',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { pageSize: 100 },
  });

  return (
    <>
      <LocalizedContentTabs fields={PET_SKIN_LANG_FIELDS} />

      <Form.Item
        label="Slug"
        name="slug"
        rules={
          slugEditable
            ? [
                { required: true },
                {
                  pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                  message: 'Lowercase kebab-case only, e.g. egg-waffle',
                },
              ]
            : []
        }
        extra={
          slugEditable
            ? 'The public skin id the app buys and equips. It cannot be changed later.'
            : 'Fixed — players already store this id as an owned skin.'
        }
      >
        <Input disabled={!slugEditable} placeholder="egg-waffle" />
      </Form.Item>

      <Form.Item
        label="Store tile artwork"
        name="imageUrl"
        rules={[{ required: true }]}
        extra={PATH_HINT}
      >
        <ArtworkInput placeholder="pet-skins/egg_waffle_card.png" />
      </Form.Item>

      <Form.Item
        label="Detail hero artwork"
        name="heroImageUrl"
        extra="The pet wearing the skin. Leave empty to reuse the tile artwork."
      >
        <ArtworkInput placeholder="pet-skins/egg_waffle_hero.png" />
      </Form.Item>

      <Form.Item
        label="Pose artwork"
        name="poseImageUrls"
        extra="In order — these fill the detail screen's Poses grid. Upload several files at once to append them."
      >
        <ArtworkListInput placeholder="pet-skins/egg_waffle_pose_1.png" />
      </Form.Item>

      <Form.Item
        label="Category"
        name="categoryId"
        extra="The Store / My Collection filter chip. Leave empty to list it under All only."
      >
        <Select {...categorySelectProps} allowClear placeholder="No category" />
      </Form.Item>

      <Form.Item label="Price (coins)" name="priceCoins" rules={[{ required: true }]}>
        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
      </Form.Item>

      <Form.Item
        label="Unlock level"
        name="unlockLevel"
        extra="Leave empty for a coins-only skin."
      >
        <InputNumber style={{ width: '100%' }} min={1} precision={0} />
      </Form.Item>

      <Form.Item label="Sort order" name="sortOrder">
        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
      </Form.Item>

      <Form.Item label="Active" name="isActive" valuePropName="checked">
        <Switch />
      </Form.Item>

      <Form.Item
        label="Coming soon"
        name="isComingSoon"
        valuePropName="checked"
        extra="Listed in the Store behind a Coming soon scrim; it cannot be claimed or bought."
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="New item tag"
        name="isNew"
        valuePropName="checked"
        extra="Draws the New item tag on the Store tile."
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="Promotion banner"
        name="isFeatured"
        valuePropName="checked"
        extra="Shows the skin in the banner carousel at the top of the Store."
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label="Banner artwork"
        name="bannerImageUrl"
        extra={`Square banner illustration. Leave empty to reuse the tile artwork. ${PATH_HINT}`}
      >
        <ArtworkInput placeholder="pet-skins/pineapple_bun_banner.png" />
      </Form.Item>

      <Form.Item
        label="Home mood animations"
        name="moodAnimationUrls"
        rules={[MOOD_SET_RULE]}
        extra={`The pet wearing this skin on Home — five animated WebPs, in order: ${MOOD_ORDER}. Leave empty to show the basic Wooki (the Pineapple Bun's set ships with the app).`}
      >
        <ArtworkListInput placeholder="pet-skins/…_mood_drained.webp" />
      </Form.Item>

      <Form.Item
        label="Home mood stills"
        name="moodImageUrls"
        rules={[MOOD_SET_RULE]}
        extra={`Five transparent PNGs matching the animations, same order: ${MOOD_ORDER}. Used for the mood picker cards and while an animation loads.`}
      >
        <ArtworkListInput placeholder="pet-skins/…_mood_drained.png" />
      </Form.Item>

      <Alert
        type="info"
        showIcon
        message="Deactivating a skin only removes it from the store — players who bought it keep wearing it."
      />
    </>
  );
};
